import type { Appointment, AppointmentStatus, Business, Client, Service, StaffMember } from "@/lib/types";

export const DISPLAY_TIME_SLOTS = [
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
  "4:30 PM",
  "5:00 PM",
];

export function timeStringToMinutes(value: string) {
  const [time, period] = value.trim().split(" ");
  const [hours, minutes] = time.split(":").map(Number);
  let normalizedHours = hours % 12;

  if (period === "PM") {
    normalizedHours += 12;
  }

  return normalizedHours * 60 + minutes;
}

export function minutesToTimeString(minutes: number) {
  const normalizedHours = Math.floor(minutes / 60);
  const minuteValue = minutes % 60;
  const period = normalizedHours >= 12 ? "PM" : "AM";
  const hours12 = normalizedHours % 12 || 12;
  return `${hours12}:${minuteValue.toString().padStart(2, "0")} ${period}`;
}

export function appointmentOverlaps(
  existing: Appointment,
  proposedStartMinutes: number,
  proposedDuration: number,
  ignoreAppointmentId?: string,
) {
  if (existing.id === ignoreAppointmentId) {
    return false;
  }

  if (existing.status === "cancelled") {
    return false;
  }

  const existingEnd = existing.startMinutes + existing.duration;
  const proposedEnd = proposedStartMinutes + proposedDuration;

  return proposedStartMinutes < existingEnd && proposedEnd > existing.startMinutes;
}

export function getAvailableTimeSlots(params: {
  appointments: Appointment[];
  staffId: string;
  date: string;
  duration: number;
  ignoreAppointmentId?: string;
}) {
  const { appointments, staffId, date, duration, ignoreAppointmentId } = params;
  const staffAppointments = appointments.filter(
    (appointment) => appointment.staffId === staffId && appointment.date === date,
  );

  return DISPLAY_TIME_SLOTS.filter((slot) => {
    const slotMinutes = timeStringToMinutes(slot);
    return !staffAppointments.some((appointment) =>
      appointmentOverlaps(appointment, slotMinutes, duration, ignoreAppointmentId),
    );
  });
}

export function getAppointmentStatusLabel(status: AppointmentStatus) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function buildAppointmentView(params: {
  appointment: Appointment;
  clients: Client[];
  services: Service[];
  staff: StaffMember[];
}) {
  const { appointment, clients, services, staff } = params;
  const client = clients.find((entry) => entry.id === appointment.clientId);
  const service = services.find((entry) => entry.id === appointment.serviceId);
  const staffMember = staff.find((entry) => entry.id === appointment.staffId);

  return {
    ...appointment,
    clientName: client?.name ?? "Unknown Client",
    clientEmail: client?.email ?? "",
    serviceName: service?.name ?? "Unknown Service",
    servicePrice: service?.price ?? 0,
    staffName: staffMember?.name ?? "Unassigned",
    timeLabel: minutesToTimeString(appointment.startMinutes),
  };
}

export function calculateOwnerStats(params: {
  appointments: Appointment[];
  services: Service[];
  clients: Client[];
  today: string;
}) {
  const { appointments, services, clients, today } = params;
  const bookedToday = appointments.filter(
    (appointment) => appointment.date === today && appointment.status !== "cancelled",
  );
  const completed = appointments.filter((appointment) => appointment.status === "completed");
  const revenueToday = bookedToday.reduce((sum, appointment) => {
    const service = services.find((entry) => entry.id === appointment.serviceId);
    return sum + (service?.price ?? 0);
  }, 0);

  return {
    todayBookings: bookedToday.length,
    revenue: revenueToday,
    newClients: clients.filter((client) => client.totalBookings <= 1).length,
    completionRate: appointments.length ? Math.round((completed.length / appointments.length) * 100) : 0,
  };
}

export function groupRevenueByMonth(params: {
  appointments: Appointment[];
  services: Service[];
}) {
  const { appointments, services } = params;
  const totals = new Map<string, number>();

  appointments
    .filter((appointment) => appointment.status !== "cancelled")
    .forEach((appointment) => {
      const monthKey = appointment.date.slice(0, 7);
      const service = services.find((entry) => entry.id === appointment.serviceId);
      totals.set(monthKey, (totals.get(monthKey) ?? 0) + (service?.price ?? 0));
    });

  return Array.from(totals.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([month, revenue]) => ({ month, revenue }));
}

export function topServices(params: {
  appointments: Appointment[];
  services: Service[];
}) {
  const { appointments, services } = params;
  const totals = new Map<string, { bookings: number; revenue: number }>();

  appointments
    .filter((appointment) => appointment.status !== "cancelled")
    .forEach((appointment) => {
      const service = services.find((entry) => entry.id === appointment.serviceId);
      if (!service) return;
      const current = totals.get(service.id) ?? { bookings: 0, revenue: 0 };
      totals.set(service.id, {
        bookings: current.bookings + 1,
        revenue: current.revenue + service.price,
      });
    });

  return services
    .map((service) => ({
      name: service.name,
      bookings: totals.get(service.id)?.bookings ?? 0,
      revenue: totals.get(service.id)?.revenue ?? 0,
    }))
    .sort((left, right) => right.revenue - left.revenue);
}

export function inferLastVisit(appointments: Appointment[]) {
  const completed = appointments
    .filter((appointment) => appointment.status === "completed")
    .sort((left, right) => right.date.localeCompare(left.date));

  return completed[0]?.date ?? null;
}

export function buildMonthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export function getBusinessSnapshot(params: {
  business: Business;
  services: Service[];
  staff: StaffMember[];
  clients: Client[];
  appointments: Appointment[];
}) {
  const { business, services, staff, clients, appointments } = params;
  return {
    business,
    services: services.filter((entry) => entry.businessId === business.id),
    staff: staff.filter((entry) => entry.businessId === business.id),
    clients: clients.filter((entry) => entry.businessId === business.id),
    appointments: appointments.filter((entry) => entry.businessId === business.id),
  };
}

